// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import {
  panelNumber,
} from "../core/display.js";
import {
  extentFromValues,
} from "../core/math.js";
import {
  cylinderKeys,
  cylinderNumberFromKey,
} from "../core/schema.js";
import {
  engineCylinderKeys,
  projectFlightDataForEngine,
} from "../core/engines.js";
import { clampScore, rankedQuantile } from "./utils.js";
import {
  chtParameters,
} from "./detectors/cht.js";

function valuesForKeys(data, keys) {
  const values = [];
  data.forEach((point) => {
    keys.forEach((key) => {
      if (Number.isFinite(point[key])) values.push(point[key]);
    });
  });
  return values;
}

export function panelPercent(value) {
  if (!Number.isFinite(value)) return "n/a";
  if (value > 0 && value < 0.1) return "<0.1%";
  const rounded = Math.round(value * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)}%`;
}

export function percentAboveThreshold(values, threshold) {
  const finite = values.filter(Number.isFinite);
  if (!finite.length || !Number.isFinite(threshold)) return null;
  const count = finite.filter((value) => value > threshold).length;
  return count / finite.length * 100;
}

export function percentBelowThreshold(values, threshold) {
  const finite = values.filter(Number.isFinite);
  if (!finite.length || !Number.isFinite(threshold)) return null;
  const count = finite.filter((value) => value < threshold).length;
  return count / finite.length * 100;
}

export function chtExposureNote({ aboveReviewThreshold, belowCautionThreshold, peak, score } = {}) {
  const reviewThreshold = panelNumber(chtParameters.reviewThresholdF, " F");
  const cautionThreshold = panelNumber(chtParameters.cautionThresholdF, " F");
  const peakText = `peak ${panelNumber(peak, " F")}`;
  if (Number.isFinite(score) && score >= 88 && Number.isFinite(belowCautionThreshold)) {
    return `CHT stayed below ${cautionThreshold} for ${panelPercent(belowCautionThreshold)} of samples · ${peakText}`;
  }
  if (!Number.isFinite(aboveReviewThreshold)) return `CHT exposure unavailable · ${peakText}`;
  if (aboveReviewThreshold <= 0) return `No CHT above ${reviewThreshold} · ${peakText}`;
  return `CHT exceeded ${reviewThreshold} for ${panelPercent(aboveReviewThreshold)} of samples · ${peakText}`;
}

export function topCylinderByPercentile(data, prefix, keys = cylinderKeys(prefix)) {
  return keys.map((key, index) => {
    const samples = data.map((point, rowIndex) => ({
      value: point[key],
      elapsedSeconds: point.elapsedSeconds ?? rowIndex,
    })).filter((sample) => Number.isFinite(sample.value));
    const values = samples.map((sample) => sample.value);
    const extent = extentFromValues(values);
    const maxSample = samples.reduce((best, sample) => {
      if (!best || sample.value > best.value) return sample;
      return best;
    }, null);
    return {
      key,
      cylinder: cylinderNumberFromKey(key) ?? index + 1,
      p95: rankedQuantile(values, 0.95),
      max: extent?.max ?? null,
      maxTime: maxSample?.elapsedSeconds ?? null,
    };
  }).filter((item) => Number.isFinite(item.p95))
    .sort((a, b) => b.p95 - a.p95)[0] ?? null;
}

function earlierPeak(left, right) {
  if (!left) return right;
  if (!right) return left;
  if (right.value !== left.value) return right.value > left.value ? right : left;
  if (Number.isFinite(right.elapsedSeconds)
    && (!Number.isFinite(left.elapsedSeconds) || right.elapsedSeconds < left.elapsedSeconds)) return right;
  return left;
}

function peakForKeys(data, keys, engine = null) {
  let peak = null;
  for (const point of data) {
    for (const key of keys) {
      if (!Number.isFinite(point[key])) continue;
      peak = earlierPeak(peak, {
        key,
        cylinder: cylinderNumberFromKey(key),
        value: point[key],
        elapsedSeconds: Number.isFinite(point.elapsedSeconds) ? point.elapsedSeconds : null,
        phase: point.phase ?? null,
      });
    }
  }
  if (!peak) return null;
  const cylinder = engine?.cylinders?.find((item) => item.chtKey === peak.key);
  return {
    ...peak,
    p95: rankedQuantile(data.map((point) => point[peak.key]).filter(Number.isFinite), 0.95),
    engineId: cylinder ? engine.id ?? engine.engineKey ?? null : null,
    engineSide: cylinder ? engine.side ?? null : null,
    displayCylinder: cylinder?.number ?? peak.cylinder,
  };
}

function chtIntervalGapLimits(data) {
  const config = chtParameters.exposure;
  const steps = data.map((point, i) => i > 0 && Number.isFinite(point.elapsedSeconds) && Number.isFinite(data[i - 1].elapsedSeconds)
    ? point.elapsedSeconds - data[i - 1].elapsedSeconds : null);
  const positive = step => Number.isFinite(step) && step > 0;
  const typical = rankedQuantile(steps.filter(positive), 0.5) ?? 1;
  return steps.map((_, i) => Math.min(config.maxGapSeconds, Math.max(typical,
    rankedQuantile(steps.slice(Math.max(1, i - 2), i + 3).filter(positive), 0.5) ?? typical) * config.maxGapSampleMultiple));
}

function chtExposureForKey(data, key, gapLimits) {
  const config = chtParameters.exposure;
  const intervals = [];
  for (let i = 1; i < data.length; i++) {
    const left = data[i - 1];
    const right = data[i];
    const seconds = right.elapsedSeconds - left.elapsedSeconds;
    if (!Number.isFinite(left.elapsedSeconds) || !Number.isFinite(right.elapsedSeconds)
      || !(seconds > 0) || seconds > gapLimits[i] || !Number.isFinite(left[key]) || !Number.isFinite(right[key])) continue;
    // Count only temperatures supported at both ends of each logged interval.
    intervals.push({ value: Math.min(left[key], right[key]), seconds });
  }
  const observedSeconds = intervals.reduce((sum, interval) => sum + interval.seconds, 0);
  const timeAbove = threshold => intervals.reduce((sum, interval) => sum + (interval.value >= threshold ? interval.seconds : 0), 0);
  let weightedP95 = null;
  let cumulativeSeconds = 0;
  for (const interval of intervals.slice().sort((a, b) => a.value - b.value)) {
    cumulativeSeconds += interval.seconds;
    if (cumulativeSeconds >= observedSeconds * 0.95) { weightedP95 = interval.value; break; }
  }
  const events = [];
  for (const thresholdF of [chtParameters.cautionThresholdF, chtParameters.peakReviewThresholdF]) {
    let start = null;
    let end = null;
    let count = 0;
    let peakF = null;
    const flush = () => {
      if (start && end && count >= config.minEventSamples && end.elapsedSeconds - start.elapsedSeconds >= config.minEventSeconds) {
        events.push({ channelKey: key, sourceCylinder: cylinderNumberFromKey(key), thresholdF,
          startTime: start.elapsedSeconds, endTime: end.elapsedSeconds,
          durationSeconds: end.elapsedSeconds - start.elapsedSeconds, sampleCount: count, peakF,
          thresholdSource: "app-screen", level: thresholdF >= chtParameters.peakReviewThresholdF ? "attention" : "watch",
          maxScore: thresholdF >= chtParameters.peakReviewThresholdF ? config.attentionScoreCeiling : config.watchScoreCeiling });
      }
      start = null; end = null; count = 0; peakF = null;
    };
    for (let i = 0; i < data.length; i++) {
      const point = data[i];
      const gap = end ? point.elapsedSeconds - end.elapsedSeconds : 0;
      if (!Number.isFinite(point.elapsedSeconds) || (end && (!(gap > 0) || gap > gapLimits[i]))) flush();
      if (!Number.isFinite(point.elapsedSeconds) || !Number.isFinite(point[key]) || point[key] < thresholdF) { flush(); continue; }
      if (!start) start = point;
      end = point; count++; peakF = Math.max(peakF ?? point[key], point[key]);
    }
    flush();
  }
  const severityFloor = events.sort((a, b) => a.maxScore - b.maxScore || b.durationSeconds - a.durationSeconds)[0] ?? null;
  return { exposure: { observedSeconds, weightedP95,
    atOrAboveCautionSeconds: timeAbove(chtParameters.cautionThresholdF),
    atOrAboveReviewSeconds: timeAbove(chtParameters.peakReviewThresholdF) }, severityFloor };
}

function chtCylinderScore(p95, max, highRatio) {
  const mildRampPenalty = Math.min(chtParameters.score.p95MildRampCapPoints,
    Math.max(0, Math.min(p95, chtParameters.cautionThresholdF) - chtParameters.reviewThresholdF) * chtParameters.score.p95MildRampPenalty);
  const score = clampScore(100 - mildRampPenalty
    - Math.max(0, p95 - chtParameters.cautionThresholdF) * chtParameters.score.p95OverCautionPenalty
    - Math.max(0, max - chtParameters.peakReviewThresholdF) * chtParameters.score.peakOverReviewPenalty
    - highRatio * chtParameters.score.cautionSampleSharePenalty);
  // Exposure below the app's Attention threshold remains at most Watch.
  return max < chtParameters.peakReviewThresholdF ? Math.max(72, score) : score;
}

function analyzeChtHealthForKeys(data, keys, engine = null) {
  const values = valuesForKeys(data, keys);
  if (!values.length) {
    return { key: "cht", score: null, note: "No CHT channel data", details: {} };
  }
  const max = extentFromValues(values)?.max ?? 0;
  const p95 = rankedQuantile(values, 0.95) ?? max;
  const highRatio = values.filter((value) => value >= chtParameters.cautionThresholdF).length / values.length;
  const aboveReviewThreshold = percentAboveThreshold(values, chtParameters.reviewThresholdF);
  const belowCautionThreshold = percentBelowThreshold(values, chtParameters.cautionThresholdF);
  const hottest = topCylinderByPercentile(data, "cht", keys);
  const peak = peakForKeys(data, keys, engine);
  const gapLimits = chtIntervalGapLimits(data);
  const cylinders = keys.map(key => {
    const samples = data.map(point => point[key]).filter(Number.isFinite);
    if (!samples.length) return null;
    const { exposure, severityFloor } = chtExposureForKey(data, key, gapLimits);
    const cylinderMax = extentFromValues(samples).max;
    const cylinderP95 = exposure.weightedP95 ?? rankedQuantile(samples, 0.95);
    const ratio = exposure.observedSeconds > 0 ? exposure.atOrAboveCautionSeconds / exposure.observedSeconds
      : samples.filter(value => value >= chtParameters.cautionThresholdF).length / samples.length;
    const score = Math.min(chtCylinderScore(cylinderP95, cylinderMax, ratio), severityFloor?.maxScore ?? 100);
    return { key, cylinder: cylinderNumberFromKey(key), score, max: cylinderMax, p95: cylinderP95, exposure, severityFloor };
  }).filter(Boolean);
  const scoringCylinder = cylinders.reduce((worst, cylinder) => cylinder.score < worst.score ? cylinder : worst);
  const { score, severityFloor } = scoringCylinder;
  const displayCylinder = engine?.cylinders?.find(cylinder => cylinder.chtKey === severityFloor?.channelKey)?.number ?? severityFloor?.sourceCylinder;
  const note = severityFloor
    ? `CHT ${displayCylinder} at or above ${severityFloor.thresholdF} F for ${Math.round(severityFloor.durationSeconds)} s; peak ${panelNumber(severityFloor.peakF, " F")}`
    : chtExposureNote({ aboveReviewThreshold, belowCautionThreshold, peak: max, score });
  return { key: "cht", score, note, details: { max, p95, highRatio, aboveReviewThreshold, belowCautionThreshold,
    hottest, peak, cylinders, scoringCylinder, severityFloor } };
}

function composeTwinChtNote(perEngine) {
  const reviewEngines = perEngine.filter((entry) => Number.isFinite(entry.score) && entry.score < 88);
  if (reviewEngines.length) {
    return reviewEngines
      .map((entry) => `${entry.shortLabel}: ${entry.note}`)
      .join("; ");
  }
  return perEngine
    .map((entry) => `${entry.shortLabel} peak ${panelNumber(entry.details?.max, " F")}`)
    .join("; ");
}

export function analyzeChtHealth(data, options = {}) {
  const descriptors = Array.isArray(options.engineDescriptors) ? options.engineDescriptors : null;
  if (descriptors && descriptors.length > 1) {
    const perEngine = descriptors.map((engine) => {
      const projected = projectFlightDataForEngine(data, engine);
      const engineKeys = engineCylinderKeys(engine, "cht")
        .filter((key) => projected.some((point) => Number.isFinite(point[key])));
      if (!engineKeys.length) return null;
      const result = analyzeChtHealthForKeys(projected, engineKeys, engine);
      return {
        engineKey: engine.engineKey ?? engine.id ?? `engine${(engine.index ?? 0) + 1}`,
        label: engine.label,
        shortLabel: engine.shortLabel ?? engine.label,
        ...result,
      };
    }).filter(Boolean);
    if (perEngine.length >= 2) {
      const worst = perEngine.reduce((acc, entry) => {
        if (acc === null) return entry;
        const accScore = Number.isFinite(acc.score) ? acc.score : 100;
        const entryScore = Number.isFinite(entry.score) ? entry.score : 100;
        if (entryScore < accScore) return entry;
        if (entryScore === accScore && (entry.details?.max ?? 0) > (acc.details?.max ?? 0)) return entry;
        return acc;
      }, null);
      const score = perEngine.reduce(
        (acc, entry) => Math.min(acc, Number.isFinite(entry.score) ? entry.score : 100),
        100,
      );
      const details = {
        ...worst.details,
        // Peak identity is independent of the engine with the worst score.
        peak: perEngine.reduce((peak, entry) => earlierPeak(peak, entry.details.peak), null),
        engines: perEngine.map((entry) => ({
          engineKey: entry.engineKey,
          label: entry.label,
          shortLabel: entry.shortLabel,
          score: entry.score,
          note: entry.note,
          details: entry.details,
        })),
        worstEngineKey: worst.engineKey,
      };
      return { key: "cht", score, note: composeTwinChtNote(perEngine), details };
    }
    if (perEngine.length === 1) {
      const only = perEngine[0];
      return { key: "cht", score: only.score, note: only.note, details: only.details };
    }
  }
  return analyzeChtHealthForKeys(data, cylinderKeys("cht"), descriptors?.[0]);
}
